/**
 * Lenis smooth scroll initialization
 *
 * @package TotalDigital
 */

import Lenis from '@studio-freight/lenis';

const isAdmin = () => {
	if (typeof window === 'undefined') {
		return false;
	}

	const url = window.location.href;
	const pathname = window.location.pathname;

	if (pathname.includes('/wp-admin/') || pathname.includes('/wp-login.php')) {
		return true;
	}

	if (document.body && document.body.classList.contains('wp-admin')) {
		return true;
	}

	if (window.self !== window.top) {
		return true;
	}

	return false;
};

// Initialize Lenis
let lenis = null;
let rafId = null;

const initLenis = () => {
	if (isAdmin()) {
		return;
	}

	try {
		lenis = new Lenis({
			duration: 1.2,
			easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
			orientation: 'vertical',
			gestureOrientation: 'vertical',
			smoothWheel: true,
			wheelMultiplier: 1,
			smoothTouch: false,
			touchMultiplier: 2,
			infinite: false,
		});

		// On-demand RAF loop — runs when user scrolls, stops when idle.
		// Lenis needs the RAF loop to process wheel/touch input, but a perpetual
		// loop prevents browser idle (hurts TTI & Lighthouse score).
		let idleTimer = null;
		const raf = (time) => {
			lenis.raf(time);
			rafId = requestAnimationFrame(raf);
		};

		const ensureRaf = () => {
			if (!rafId) {
				rafId = requestAnimationFrame(raf);
			}
			clearTimeout(idleTimer);
			idleTimer = setTimeout(() => {
				if (rafId) {
					cancelAnimationFrame(rafId);
					rafId = null;
				}
			}, 500);
		};

		// Listen to native input events (before Lenis processes them).
		window.addEventListener('wheel', ensureRaf, { passive: true });
		window.addEventListener('touchstart', ensureRaf, { passive: true });
		window.addEventListener('keydown', (e) => {
			if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(e.key)) {
				ensureRaf();
			}
		}, { passive: true });

		// Also restart on programmatic scrollTo calls (via Swup, etc).
		lenis.on('scroll', ensureRaf);

		// Initial tick so Lenis measures the page.
		ensureRaf();

	} catch (error) {
		console.warn('Lenis initialization error:', error);
	}
};

const destroyLenis = () => {
	if (rafId) {
		cancelAnimationFrame(rafId);
		rafId = null;
	}
	if (lenis) {
		lenis.destroy();
		lenis = null;
	}
};

document.addEventListener('DOMContentLoaded', initLenis);

document.addEventListener('swup:content:replace', () => {
	if (lenis) {
		lenis.resize();
	}
});

export const getLenis = () => lenis;
export default getLenis;
